import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";  
import "./Guides.css";
import backend from "../../../../../api";

/* MUI */
import {
  Container,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import MosqueRoundedIcon from "@mui/icons-material/MosqueRounded";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const FALLBACK =
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1200&auto=format&fit=crop";

const slugify = (s) =>
  s.toLowerCase().replace(/[^\w]+/g, "-").replace(/(^-|-$)/g, "");

export default function GuidesList() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const userId = user?._id;
  const role = user?.role;
  const isAdmin = role === "admin";

  const [tab, setTab] = useState("ritual");
  const [q, setQ] = useState("");
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchGuides = async () => {
    try {
      setLoading(true);
      const { data } = await backend.get("/api/content");
      setGuides(data?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuides();
  }, []);

  const filteredGuides = useMemo(() => {
    const byCat = guides.filter((g) => g.category === tab);
    if (!q.trim()) return byCat;
    const s = q.toLowerCase();
    return byCat.filter(
      (g) =>
        g.title.toLowerCase().includes(s) ||
        g.excerpt.toLowerCase().includes(s)
    );
  }, [tab, q, guides]);

  const openDetail = (g) => {
    const slug = g.slug || slugify(g.title);
    navigate(`/guides/${slug}`, {
      state: { guideId: g._id },
    });
  };

  const handleDelete = async (id) => {
    if (!isAdmin || !userId) return;
    const ok = window.confirm("Are you sure you want to delete this guide?");
    if (!ok) return;

    try {
      setDeletingId(id);
      await backend.delete(`/api/content/${userId}/${id}`);
      setGuides((prev) => prev.filter((g) => g._id !== id));
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="gl-wrap">
      {/* Hero */}
      <div className="gl-hero">
        <Container maxWidth="lg" className="gl-heroInner">
          <h1 className="gl-h1">Guides</h1>
          <p className="gl-sub">
            From planning to rituals — clear, respectful guidance in one place.
          </p>

          <div className="gl-tabs">
            <Chip
              className={`gl-tab ${tab === "ritual" ? "is-active" : ""}`}
              icon={<MosqueRoundedIcon />}
              label="Ritual guidance"
              onClick={() => setTab("ritual")}
            />
            <Chip
              className={`gl-tab ${tab === "pretravel" ? "is-active" : ""}`}
              icon={<FlightTakeoffRoundedIcon />}
              label="Pre-travel guide"
              onClick={() => setTab("pretravel")}
            />
          </div>

          <TextField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search"
            fullWidth
            className="gl-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon />
                </InputAdornment>
              ),
            }}
          />
        </Container>
      </div>

      {/* Grid */}
      <Container maxWidth="lg" className="gl-container">
        {loading && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1.5, color: "text.secondary" }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2">Loading guides...</Typography>
          </Stack>
        )}
        <Grid container spacing={2.5}>
          {filteredGuides.map((g) => (
            <Grid
              key={g._id}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              className="gl-grid-item"
            >
              <Card className="gl-card" elevation={0}>
                <Box sx={{ position: "relative" }}>
                  {isAdmin && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(g._id);
                      }}
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        zIndex: 2,
                        bgcolor: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "#FFEBEE" },
                      }}
                      disabled={deletingId === g._id}
                    >
                      {deletingId === g._id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteOutlineIcon
                          fontSize="small"
                          sx={{ color: "#E53935" }}
                        />
                      )}
                    </IconButton>
                  )}

                  <CardActionArea onClick={() => openDetail(g)}>
                    <CardMedia
                      component="img"
                      alt={g.title}
                      image={g.imageUrl || FALLBACK}
                      onError={(e) => (e.currentTarget.src = FALLBACK)}
                      className="gl-cardImg"
                    />
                    <CardContent className="gl-cardBody">
                      <Typography className="gl-cardTitle" variant="subtitle1">
                        {g.title}
                      </Typography>
                      <Typography className="gl-cardExcerpt" variant="body2">
                        {g.excerpt}
                      </Typography>
                      <Button className="gl-readMore">Read More</Button>
                    </CardContent>
                  </CardActionArea>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </div>
  );
}
